<script setup lang="ts">
import { useRouter } from 'vue-router';
import { ICON_TYPE, GREETING_COLOR, GREETING_HOVER_COLOR } from '@/ai/utils/defined';
import TargetList from '@/ai/components/TargetList.vue';
import ChartSection from '@/analysis/components/sections/ChartSection.vue';
import AlarmRecommendTable from '@/ai/components/AlarmRecommendTable.vue';
import StructuredAnswer from '@/ai/components/StructuredAnswer.vue';
import InvestigationPathCard from '@/ai/components/InvestigationPathCard.vue';
import type { AlarmRecommendation, AlarmRecommendMetric } from '@/ai/utils/types';
import { type Props, setup, type Emit } from './botMessage.setup';
import { useChatStore } from '@/ai/store/chat';
import { useInternational } from '@/common/locale';

const props = defineProps<Props>();

const emit = defineEmits<Emit>();

const {
  parsedResult,
  handleClick,
  container,
  parsedMessage,
  parsedReport,
  inlineCharts,
  alarmRecs,
  structuredCard,
  investigationCard,
  suggestions,
  toolCallChips,
  isPartial,
  statusText,
  responseMetaLine,
  messageTime,
  messageTimeFull,
  handleSuggestionClick,
} = setup(props, emit);

const chatStore = useChatStore();
const router = useRouter();
const { t } = useInternational();

const openReport = (e: MouseEvent) => {
  if (!parsedReport.value) return;
  e.preventDefault();
  router.push({
    name: 'AnalysisReportDetail',
    params: { reportId: parsedReport.value.reportId },
  });
};

/**
 * 알람 임계치 추천 표의 행별 '알람 추가' — chat store 가 추천값을 prefill 로 등록하고
 * 사용자 알람 규칙 생성 화면(/alert/userAlert)으로 이동시킨다. 실제 생성(저장)은 화면에서.
 */
const onAddAlarm = (payload: { rec: AlarmRecommendation; metric: AlarmRecommendMetric }) => {
  chatStore.applyAlarmRecommendation(payload);
};

/**
 * markdown 으로 렌더된 본문 안의 anchor (`target://kind/sub/<id>`) 클릭을 가로채
 * chat store 의 open_target_detail 동등 흐름으로 dispatch.
 * Gemini 가 list_targets_with_metrics 결과를 표로 렌더할 때 이름 셀에 사용.
 */
/**
 * 36-char UUID 형식 (8-4-4-4-12, hex) 검증.
 * Gemini 가 가끔 origin_name 의 마지막 segment (예: 'fcd5999c9-d9hp6') 또는 truncated prefix
 * (예: 'd80b5469') 를 target_id 자리에 박는데, 이는 클릭 시 backend 가 instance/WAS 를
 * 찾지 못해 5xx/4xx 로 이어진다. 클라이언트에서 형식 검증으로 차단해 사용자에게 plain
 * text 클릭 + 무동작 으로 graceful fallback.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * detail slide-out 의 selectedTab 식별자 형식.
 * camelCase ASCII identifier (예: 'lockInfo', 'activeSession', 'metric').
 * 한글 / 공백 / 특수문자가 들어가면 silent drop.
 */
const TAB_ID_RE = /^[a-z][a-zA-Z0-9]{1,30}$/;

const onBodyClick = (e: MouseEvent) => {
  const a = (e.target as HTMLElement | null)?.closest?.('a');
  if (!a) return;
  // target:// anchor 는 botMessage.setup 에서 href="#" + data-target 으로 재작성되어 있음.
  const data = a.getAttribute('data-target');
  if (!data) {
    // DOMPurify 가 data-target 을 strip 했거나, target:// anchor 가 아닌 일반 외부 링크인 경우.
    // href 를 보고 target:// 면 strip 으로 판단해 경고.
    const href = a.getAttribute('href');
    console.info('[chatbot] BotMessage anchor click — no data-target', {
      href,
      text: a.textContent?.trim(),
    });
    return;
  }
  e.preventDefault();
  // data-target 은 'kind/sub/uuid' 또는 'kind/sub/uuid?tab=lockInfo' 형식.
  // path 와 query string 분리 후 path 만 segment 로 split.
  const qIdx = data.indexOf('?');
  const pathPart = qIdx === -1 ? data : data.slice(0, qIdx);
  const queryPart = qIdx === -1 ? '' : data.slice(qIdx + 1);
  const m = pathPart.split('/');
  if (m.length < 3) {
    console.warn('[chatbot] BotMessage data-target malformed:', data);
    return;
  }
  const [kind, sub, ...rest] = m;
  const targetId = rest.join('/');
  if (!targetId) {
    console.warn('[chatbot] BotMessage data-target missing targetId:', data);
    return;
  }
  // target_id 가 UUID 형식이 아니면 dispatch 하지 않는다 (placeholder '_' / 잘린 prefix /
  // pod-name fragment 같은 invalid id 를 backend 로 보내 5xx 가 나는 것을 차단).
  if (targetId === '_' || !UUID_RE.test(targetId)) {
    console.warn(
      '[chatbot] BotMessage target_id is not a valid 36-char UUID — link click ignored',
      { kind, sub, targetId, text: a.textContent?.trim() },
    );
    return;
  }
  // 선택적 tab hint — 형식 검증 후 silent drop. dispatch 자체는 진행.
  let tab: string | undefined;
  if (queryPart) {
    const params = new URLSearchParams(queryPart);
    const rawTab = params.get('tab');
    if (rawTab && TAB_ID_RE.test(rawTab)) {
      tab = rawTab;
    } else if (rawTab) {
      console.warn('[chatbot] BotMessage invalid tab id — falling back to default', {
        tab: rawTab,
        kind,
        sub,
        targetId,
      });
    }
  }
  const name = a.textContent?.trim() || targetId;
  console.info('[chatbot] BotMessage dispatch target detail', {
    kind,
    sub,
    targetId,
    name,
    tab,
  });
  chatStore.dispatchTargetDetailFromBot({
    type: kind as never,
    target_id: targetId,
    target_name: name,
    sub_kind: sub === '_' ? undefined : sub,
    tab,
  });
};
</script>

<template>
  <div class="bot-message__wrapper">
    <div
      v-if="message.messageType === 'welcomeText'"
      class="welcome-message__container"
    >
      <div class="bot-message__animation">
        <div
          ref="container"
          class="bot-animation"
        />
        <div
          v-dompurify-html="parsedMessage"
          class="bot-message"
        />
      </div>
      <div class="welcome-message__content">
        <div
          v-for="(item, index) in parsedResult"
          :key="index"
          class="bot-button__container"
          :style="{
            '--color': GREETING_COLOR[index],
            '--hover-color': GREETING_HOVER_COLOR[index],
          }"
          @click="handleClick(item.message)"
        >
          <div class="icon-box">
            <ev-icon
              class="bot-button__icon"
              :icon="ICON_TYPE[index]"
              :style="{ '--color': GREETING_COLOR[index] }"
            />
          </div>

          <div class="bot-button__text">{{ item.message }}</div>
          <ev-icon
            icon="ev-icon-allow2-right2"
            class="line-arrow-right"
            :style="{ '--color': GREETING_COLOR[index] }"
          />
        </div>
      </div>
    </div>
    <div
      v-else
      class="general-message__container"
    >
      <div class="bot-avatar">
        <i class="bot-avatar__inner" />
        <span
          v-if="isPartial"
          class="bot-avatar__pulse"
        />
      </div>
      <div class="general-message__content">
        <!--
          Lightweight overall 진행 indicator — chip 시스템(streamToolEvents)과 무관하게
          partial 동안 항상 표시. statusKind 따라 "생각 중..." 또는 "{도구 라벨} 중...".
          chip 과 공존 — chip 은 detail (args/result/latency), status 는 overall 한 줄.
        -->
        <div
          v-if="statusText"
          class="bot-status-line"
          role="status"
          aria-live="polite"
        >
          <span class="bot-status-dot" />
          <span class="bot-status-text">{{ statusText }}</span>
        </div>
        <!-- 도구 호출 진행 chip 묶음 — streaming 중에 점점 추가됨 -->
        <div
          v-if="toolCallChips.length"
          class="tool-chip-strip"
        >
          <div
            v-for="chip in toolCallChips"
            :key="chip.key"
            class="tool-chip"
            :class="{
              'tool-chip--running': chip.status === 'running',
              'tool-chip--ok': chip.status === 'ok',
              'tool-chip--error': chip.status === 'error',
            }"
          >
            <ev-icon
              v-if="chip.status === 'running'"
              icon="ev-icon-refresh"
              class="tool-chip__icon tool-chip__icon--spin"
            />
            <ev-icon
              v-else-if="chip.status === 'ok'"
              icon="ev-icon-check-on"
              class="tool-chip__icon"
            />
            <ev-icon
              v-else
              icon="ev-icon-warning"
              class="tool-chip__icon"
            />
            <span class="tool-chip__label">{{ chip.label }}</span>
            <span
              v-if="chip.latencyText"
              class="tool-chip__latency"
            >
              {{ chip.latencyText }}
            </span>
          </div>
        </div>

        <img
          v-if="message.imageUrl"
          class="widget-message__wrapper"
          :src="message.imageUrl"
          alt="Capture Widget"
        />
        <div
          v-if="message.message || isPartial"
          class="text-message__wrapper"
          :class="{ 'text-message__wrapper--partial': isPartial }"
          @mousedown.stop
        >
          <div
            v-dompurify-html="parsedMessage"
            class="text-message__body"
            @click="onBodyClick"
          />
          <span
            v-if="isPartial"
            class="text-message__cursor"
            aria-hidden="true"
          />
        </div>
        <!--
          구조화 응답 카드 — present_structured_answer 결과를 고정 레이아웃으로 렌더(provider-agnostic).
          done 후에만 채워지므로 partial 중엔 미렌더.
        -->
        <structured-answer
          v-if="!isPartial && structuredCard"
          :answer="structuredCard"
        />
        <!--
          인라인 차트 — 추이/Top-N 도구 결과를 백엔드가 자동 첨부 (리포트 chart 섹션 스키마,
          ChartSection 재사용). 스트리밍 종료(done) 후에만 chartsJson 이 채워지므로 partial 중엔 미렌더.
        -->
        <div
          v-if="!isPartial && inlineCharts.length > 0"
          class="bot-message__charts"
          data-testid="bot-inline-charts"
        >
          <chart-section
            v-for="(c, idx) in inlineCharts"
            :key="`inline-chart-${idx}`"
            :section="c"
            class="bot-message__chart"
          />
        </div>
        <!--
          알람 임계치 추천 표 — recommend_alarm_thresholds 결과를 백엔드가 구조화 attach.
          행별 '알람 추가' → 알람 규칙 생성 화면 prefill. 스트리밍 종료 후에만 채워짐.
        -->
        <div
          v-if="!isPartial && alarmRecs.length > 0"
          class="bot-message__alarm-recs"
          data-testid="bot-alarm-recs"
        >
          <alarm-recommend-table
            v-for="(r, idx) in alarmRecs"
            :key="`alarm-rec-${idx}`"
            :rec="r"
            @add-alarm="onAddAlarm"
          />
        </div>
        <!--
          응답 메타 라인 — 모델/토큰/소요시간. 환경설정(responseMetaOptions)에서 항목별
          on/off. 모두 off 이거나 metaJson 없는 (구) 메시지는 미렌더.
        -->
        <div
          v-if="responseMetaLine"
          class="bot-message__meta"
          data-testid="bot-response-meta"
        >
          {{ responseMetaLine }}
        </div>
        <div
          v-if="message.resultType === 'textButton'"
          class="button-message__wrapper"
        >
          <div
            v-for="(item, index) in parsedResult"
            :key="index"
            class="button-message__container"
            @click="handleClick(item.message)"
          >
            <div class="button-message__text">{{ item.message }}</div>
            <ev-icon
              icon="ev-icon-allow2-right2"
              class="line-arrow-right"
            />
          </div>
        </div>
        <div
          v-if="message.resultType === 'targetList'"
          class="target-message__wrapper"
        >
          <target-list :target-data="parsedResult" />
        </div>
        <!--
          연계 조사 카드 (resultType 'investigation') — 확장 조사 배너 + 추적 경로 체인 +
          연계 분석 버튼. 파싱 실패 시 investigationCard 가 null 이라 분기 자체 미렌더.
          액션 클릭은 suggestion pill 과 동일한 전송 흐름(suggestion-click) 재사용.
        -->
        <investigation-path-card
          v-if="investigationCard"
          :path="investigationCard"
          @action-click="handleSuggestionClick"
        />
        <!--
          generate_report 도구가 만든 메시지: 단순 라운딩 버튼 1개 ("자세히 보기").
          parsedReport === null (파싱 실패) 시 분기 자체 미렌더 — 본문 텍스트만 보임.
          본문 풍선이 답변 의미를 담고, 이 버튼은 차트/표 상세 화면 진입만 담당.
        -->
        <button
          v-if="parsedReport"
          type="button"
          class="report-open-btn"
          data-testid="report-card-open"
          @click="openReport"
        >
          {{ t('WORD.ANALYSIS_REPORT.OPEN_DETAIL') }}
        </button>
        <!--
          후속 질문 추천 pill 영역 — bot 답변 끝의 [SUGGESTIONS]\n- ...\n[/SUGGESTIONS]
          블록을 parser 가 분리해 string[] 으로 노출. streaming 중 closing tag 미수신
          이면 빈 배열이라 미렌더 (partial 노출 회피).
        -->
        <div
          v-if="!isPartial && suggestions.length > 0"
          class="bot-message__suggestions"
        >
          <button
            v-for="(s, i) in suggestions"
            :key="`sug-${i}`"
            type="button"
            class="suggestion-pill"
            @click="handleSuggestionClick(s)"
          >
            {{ s }}
          </button>
        </div>
        <!-- 답변 받은 시각 — 내용 아래 좌측, 흐린 캡션. partial(스트리밍 중) 엔 미표시. -->
        <span
          v-if="messageTime && !isPartial"
          class="bot-message__time"
          :title="messageTimeFull"
        >
          {{ messageTime }}
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.bot-message__wrapper {
  display: flex;
  flex-direction: column;

  .line-arrow-right {
    width: 16px;
    height: 16px;
    margin-left: 8px;
  }

  .welcome-message__container {
    flex-direction: column;
    width: 382px;
    margin: 0 auto;

    .bot-message__animation {
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
    }

    .bot-animation {
      align-self: end;
      width: 110px;
      height: 140px;
    }

    .bot-message {
      margin-bottom: 34px;
      font-size: 18px;
      font-weight: 400;
      line-height: 28px;
      color: var(--color-gray-01-11);
    }
  }

  .welcome-message__content {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;

    .icon-box {
      display: flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      margin-right: 12px;
      border-radius: 8px;
      background-color: var(--color-gray-09-00);
    }

    .bot-button__icon {
      flex-shrink: 0;
      width: 16px;
      height: 16px;
      margin: 4px;
      background-color: var(--color);
      mask-repeat: no-repeat;
      mask-size: contain;
    }

    .bot-button__text {
      flex: 1;
      font-size: 12px;
      font-weight: 700;
      line-height: 14px;
      color: var(--color-gray-00-11);
    }

    .line-arrow-right {
      width: 16px;
      height: 16px;
      margin-left: 8px;
    }
  }

  .bot-button__container {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 8px;
    cursor: pointer;
    border: 1px solid transparent;
    border-radius: 8px;
    background: var(--color-gray-07-00);
    box-shadow: 0 10px 26px 0 var(--ai-chat-bubble-shadow-color);
  }

  .bot-button__container:hover {
    border-color: var(--color);
    .bot-button__text {
      color: var(--hover-color);
    }

    .line-arrow-right {
      color: var(--hover-color);
    }
  }

  /* ======== 모던 일반 메시지 영역 ======== */
  .general-message__container {
    display: flex;
    gap: 10px;
    align-items: flex-start;
  }

  .bot-avatar {
    position: relative;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: radial-gradient(circle at 30% 30%, #6c4fff 0%, #3c8eff 60%, #2563eb 100%);
    box-shadow: 0 6px 18px -4px rgb(60, 142, 255, 0.45);
  }

  .bot-avatar__inner {
    position: absolute;
    border-radius: 50%;
    background-color: rgb(255, 255, 255, 0.92);
    background-image: url('@/common/assets/ai/chat-bot.svg');
    background-position: center;
    background-size: contain;
    background-repeat: no-repeat;
    inset: 6px;
  }

  .bot-avatar__pulse {
    position: absolute;
    pointer-events: none;
    border: 2px solid rgb(60, 142, 255, 0.55);
    border-radius: 50%;
    animation: bot-avatar-pulse 1.4s ease-out infinite;
    inset: -3px;
  }

  @keyframes bot-avatar-pulse {
    0% {
      opacity: 0.85;
      transform: scale(0.95);
    }
    70% {
      opacity: 0;
      transform: scale(1.25);
    }
    100% {
      opacity: 0;
      transform: scale(1.25);
    }
  }

  .general-message__content {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 6px;
    width: 80%;
    min-width: 0;
  }

  .widget-message__wrapper {
    width: 75%;
    margin-bottom: 8px;
    overflow: hidden;
    border-radius: 12px;
    background: var(--color-gray-07-00);
    box-shadow: 0 10px 26px 0 var(--ai-chat-bubble-shadow-color);
  }

  /* ======== Bot status line ========
     partial 동안 표시되는 lightweight 진행 indicator (생각 중... / X 중...).
     chip 보다 가벼운 회색 텍스트 + 깜빡이는 점. chip 과 공존 가능. */
  .bot-status-line {
    display: inline-flex;
    gap: 8px;
    align-items: center;
    padding: 3px 0;
    margin-bottom: 2px;
    user-select: none;
    font-size: 11.5px;
    font-weight: 500;
    color: var(--color-gray-04-11);
  }
  .bot-status-dot {
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--color-blue-04-04);
    animation: bot-status-dot-pulse 1.1s ease-in-out infinite;
  }
  .bot-status-text {
    font-style: italic;
    letter-spacing: 0.1px;
  }

  @keyframes bot-status-dot-pulse {
    0%,
    100% {
      opacity: 0.35;
      transform: scale(0.8);
    }
    50% {
      opacity: 1;
      transform: scale(1.1);
    }
  }

  /* ======== Tool call chip strip ======== */
  .tool-chip-strip {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 2px;
  }

  .tool-chip {
    display: inline-flex;
    gap: 6px;
    align-items: center;
    padding: 4px 10px 4px 8px;
    border: 1px solid var(--color-gray-05-03);
    border-radius: 999px;
    background: var(--color-gray-09-00);
    font-size: 11.5px;
    font-weight: 500;
    line-height: 16px;
    color: var(--color-gray-01-11);
    transition:
      background 0.15s ease,
      border-color 0.15s ease,
      color 0.15s ease;
  }

  .tool-chip__icon {
    width: 12px;
    height: 12px;
  }

  .tool-chip__icon--spin {
    animation: tool-chip-spin 0.9s linear infinite;
  }

  @keyframes tool-chip-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .tool-chip__label {
    white-space: nowrap;
  }

  /* 칩 배경이 상태색 진한 톤(-09)이라 고정 회색 숫자는 묻힘 — 라벨색(currentcolor)을
     흰색 쪽으로 밝혀 어떤 상태색(빨강/초록/파랑) 위에서도 또렷하게 읽히도록 한다. */
  .tool-chip__latency {
    font-size: 10.5px;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    color: color-mix(in srgb, currentcolor 45%, #ffffff);
  }

  .tool-chip--running {
    border-color: var(--color-blue-04-04);
    background: var(--color-blue-09-09);
    color: var(--color-blue-04-04);
  }

  .tool-chip--ok {
    border-color: var(--color-green-05-05);
    background: var(--color-green-09-09);
    color: var(--color-green-05-05);
  }

  .tool-chip--error {
    border-color: var(--color-red-05-05);
    background: var(--color-red-09-09);
    color: var(--color-red-05-05);
  }

  /* ======== Text bubble ======== */
  .text-message__wrapper {
    position: relative;
    width: fit-content;
    max-width: 92%;
    padding: 12px 14px;
    border-radius: 14px 14px 14px 4px;
    background: var(--color-gray-07-00);
    box-shadow: 0 10px 26px 0 var(--ai-chat-bubble-shadow-color);
    font-size: 13px;
    font-weight: 400;
    line-height: 1.55;
    color: var(--color-gray-00-11);
  }

  .text-message__wrapper--partial {
    border: 1px solid var(--color-blue-06-04);
  }

  /* markdown 으로 렌더되는 본문. block 으로 두어 <h2>/<ul>/<p> 등의 블록 요소가
     원래 의도대로 줄바꿈 + 들여쓰기 + 간격을 갖도록 한다. */
  .text-message__body {
    display: block;
    overflow-wrap: anywhere;

    > *:first-child {
      margin-top: 0;
    }
    > *:last-child {
      margin-bottom: 0;
    }

    p {
      margin: 0 0 8px;
    }
    p:last-child {
      margin-bottom: 0;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      margin: 12px 0 6px;
      font-weight: 700;
      line-height: 1.35;
      color: var(--color-blue-04-04);
    }
    h1 {
      font-size: 15px;
    }
    h2 {
      font-size: 14px;
    }
    h3 {
      font-size: 13.5px;
    }
    h4,
    h5,
    h6 {
      font-size: 13px;
    }

    ul,
    ol {
      padding-left: 20px;
      margin: 4px 0 8px;
    }
    li {
      margin: 2px 0;
    }
    li > p {
      margin: 0;
    }

    /* 중첩 리스트 들여쓰기 */
    li > ul,
    li > ol {
      margin: 2px 0;
    }

    blockquote {
      padding: 6px 10px;
      margin: 6px 0;
      border-left: 3px solid var(--color-blue-06-04);
      background: var(--color-gray-08-01);
      color: var(--color-gray-02-11);
    }

    hr {
      margin: 10px 0;
      border: none;
      border-top: 1px solid var(--color-gray-05-03);
    }

    a {
      color: var(--color-blue-04-04);
      text-decoration: underline;
      text-underline-offset: 2px;

      &:hover {
        text-decoration: none;
      }
    }

    table {
      width: auto;
      max-width: 100%;
      margin: 6px 0;
      border-collapse: collapse;
      font-size: 12px;

      th,
      td {
        padding: 4px 8px;
        border: 1px solid var(--color-gray-05-03);
        text-align: left;
      }
      th {
        background: var(--color-gray-08-01);
        font-weight: 600;
      }
    }

    code {
      padding: 1px 5px;
      border-radius: 4px;
      background: var(--color-gray-08-01);
      font-family: var(--font-family-code);
      font-size: 12px;
    }

    pre {
      padding: 10px 12px;
      margin: 6px 0;
      overflow-x: auto;
      border-radius: 8px;
      background: var(--color-gray-08-01);

      code {
        padding: 0;
        background: transparent;
        font-size: 11.5px;
        line-height: 1.5;
      }
    }

    strong {
      font-weight: 600;
      color: var(--color-blue-04-04);
    }

    em {
      font-style: italic;
      color: var(--color-gray-01-11);
    }

    img {
      max-width: 100%;
      border-radius: 6px;
    }
  }

  /* ======== Inline charts (추이/Top-N 자동 첨부) ========
     리포트 ChartSection 재사용 — 챗 폭(480px)에 맞게 컴팩트 오버라이드. */
  .bot-message__charts {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    max-width: 92%;
  }

  .bot-message__chart {
    &.report-chart {
      padding: 10px 12px 12px;
      margin: 0;
      box-shadow: 0 10px 26px 0 var(--ai-chat-bubble-shadow-color);
    }

    .report-chart__title {
      font-size: 12.5px;
    }

    .report-chart__canvas {
      height: 180px;
    }
  }

  /* ======== Response meta line ========
     응답 풍선 바로 아래 — 모델 · 토큰 · 소요시간. 환경설정 항목별 on/off. */
  .bot-message__meta {
    padding: 0 2px;
    margin-top: -2px;
    user-select: text;
    font-size: 10.5px;
    font-weight: 400;
    line-height: 14px;
    color: var(--color-gray-04-11);
  }

  /* 답변 받은 시각 — 내용 아래 좌측, 흐린 캡션 (meta 라인과 동일 톤). */
  .bot-message__time {
    padding: 0 2px;
    user-select: none;
    font-size: 10.5px;
    font-weight: 400;
    line-height: 14px;
    color: var(--color-gray-04-11);
    font-variant-numeric: tabular-nums;
  }

  /* streaming 중에 마지막 토큰 뒤에 깜빡이는 커서 표시 — block body 옆에 inline-block 으로 배치 */
  .text-message__cursor {
    display: inline-block;
    width: 2px;
    height: 14px;
    margin-left: 2px;
    vertical-align: middle;
    background: var(--color-blue-04-04);
    animation: text-cursor-blink 1s steps(2) infinite;
  }

  @keyframes text-cursor-blink {
    50% {
      opacity: 0;
    }
  }

  .button-message__wrapper {
    display: flex;
    flex-direction: column;
    width: 75%;
    padding: 8px;
    margin-top: 8px;
    border-radius: 8px;
    background: var(--color-gray-07-00);
    box-shadow: 0 10px 26px 0 var(--ai-chat-bubble-shadow-color);
    font-size: 11px;
    font-weight: 500;
    color: var(--color-gray-02-11);
  }

  .button-message__wrapper:hover {
    color: var(--color-gray-04-11);
    .line-arrow-right {
      color: var(--color-gray-04-11);
    }
  }

  .button-message__container {
    display: flex;
    justify-content: space-between;
    padding: 18px 10px;
    cursor: pointer;
    border-bottom: 1px solid var(--color-gray-05-03);

    &:last-child {
      margin-bottom: 0;
      border-bottom: none;
    }
  }

  .button-message__container:hover {
    color: var(--ai-chat-bot-message-hover-color);
    .line-arrow-right {
      color: var(--ai-chat-bot-message-hover-color);
    }
  }

  /* ======== Report open button (generate_report 결과) ========
     본문 풍선이 답변 의미를 담고, 이 버튼은 상세 화면 진입만. 단순 라운딩 outline 버튼. */
  .report-open-btn {
    align-self: flex-start;
    padding: 7px 16px;
    margin-top: 4px;
    cursor: pointer;
    border: 1px solid var(--color-blue-04-04);
    border-radius: 999px;
    background: transparent;
    font-size: 12px;
    font-weight: 500;
    color: var(--color-blue-04-04);
    transition:
      background 0.15s ease,
      color 0.15s ease;

    &:hover {
      background: var(--color-blue-04-04);
      color: #ffffff;
    }
    &:active {
      transform: translateY(1px);
    }
  }

  /* ======== Suggestion pills (후속 질문 추천) ========
     bot 답변 끝의 [SUGGESTIONS] 블록을 클릭 가능한 pill 로 렌더.
     - 좌측 정렬 + multi-line 허용 (80자 초과 줄바꿈 자연스럽게)
     - 클릭 시 ChatWindow 가 입력창에 채움 (자동 전송 X) */
  .bot-message__suggestions {
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: fit-content;
    max-width: 92%;
    margin-top: 8px;
  }

  .suggestion-pill {
    display: block;
    width: 100%;
    padding: 8px 12px;
    margin: 0;
    cursor: pointer;
    border: 1px solid var(--color-blue-06-04);
    border-radius: 16px;
    background: var(--color-gray-09-00);
    font-size: 12px;
    font-weight: 500;
    line-height: 1.4;
    color: var(--color-blue-04-04);
    text-align: left;
    white-space: normal;
    transition:
      background 0.15s ease,
      border-color 0.15s ease,
      color 0.15s ease;
    overflow-wrap: anywhere;

    &::before {
      opacity: 0.8;
      content: '💡 ';
    }

    &:hover {
      border-color: var(--color-blue-04-04);
      background: var(--color-blue-09-09);
      color: var(--color-blue-03-03);
    }

    &:active {
      transform: translateY(1px);
    }
  }
}
</style>
